<template>
  <div class="container">
    <div ref="vertList">
      <template v-for="(searchResult, index) in searchResults">
        <div :key="searchResult.cursor">
          <SitterCard
            :sitterIdForProfile="searchResult.sitterIdForProfile"
            :distance="searchResult.distance"
            :description="searchResult.description"
            :minRate="searchResult.minRate"
            :maxRate="searchResult.maxRate"
            :name="searchResult.name"
            :photoUrl="searchResult.photoUrl"
            :yearsExperience="searchResult.yearsExperience"
            :avgRating="searchResult.avgRating"
            :totalRatings="searchResult.totalRatings"
            :idv="searchResult.idv"
            :badges="searchResult.badges"
            :caretype="caretypeForSitter(caretype, searchResult.sitter)"
            customClasses="w-full mb-20"
            :availability="searchResult.availability"
            :city="searchResult.city"
            :stateAbbr="searchResult.stateAbbr"
            :isRecruited="isRecruited(searchResult.sitterIdForProfile)"
            :account="account"
            :showInviteButton="showInviteButton"
            :searchCareType="searchCareType"
            :parentJobs="parentJobs"
            target="_blank"
            @sitterRecruited="handleSitterRecruited"
          />
          <BHPartnersList v-if="showExtraContent(index)" :key="index" :showHeader="false" :zipCode="zip" />
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import debounce from 'lodash/debounce';
import SitterCard from '@/components/sitter_card/SitterCard.vue';
import BHPartnersList from '@/components/bh_partners/BHPartnersList.vue';
import { Account } from '@/models/account';
import { JobEdge } from '@/graphql/messages/queries';

export default {
  name: 'SearchList',
  components: {
    SitterCard,
    BHPartnersList,
  },
  props: {
    searchResults: {
      required: true,
      type: Array,
    },
    caretype: {
      required: true,
      type: String,
    },
    zip: {
      type: String,
      required: true,
    },
    showBHPartners: {
      type: Boolean,
      default: true,
    },
    availabilityChecks: {
      type: Array,
      default: () => [],
    },
    account: {
      type: Object as PropType<Account | null>,
      default: null,
    },
    showInviteButton: {
      type: Boolean,
      default: false,
    },
    searchCareType: {
      type: String,
      default: 'babysitter',
    },
    parentJobs: {
      type: Array as PropType<JobEdge[]>,
      default: () => [],
    },
  },
  data(): any {
    return {
      debouncedScroll: null,
      isLoadingMore: false,
      timeoutId: null,
      scrollContainer: null as HTMLElement | null,
    };
  },
  mounted() {
    this.debouncedScroll = debounce(this.handleScroll, 100);
    this.scrollContainer = this.$el.closest('.overlay__main--scrollable') as HTMLElement;
    this.scrollContainer.addEventListener('scroll', this.debouncedScroll);
  },
  beforeDestroy() {
    this.scrollContainer.removeEventListener('scroll', this.debouncedScroll);
    if (this.timeoutId) clearTimeout(this.timeoutId);
    // Prevent memory leaks by cancelling any pending debounced calls
    if (this.debouncedScroll && this.debouncedScroll.cancel) {
      this.debouncedScroll.cancel();
    }
  },
  methods: {
    isRecruited(sitterUuid: string) {
      return this.availabilityChecks?.includes(sitterUuid);
    },
    handleScroll() {
      if (!this.isLoadingMore && this.$refs.vertList) {
        const containerHeight = this.scrollContainer.clientHeight;
        const listBottom = this.$refs.vertList.getBoundingClientRect().bottom;
        const containerBottom = this.scrollContainer.getBoundingClientRect().top + containerHeight;

        // Trigger when we're within 150px of the bottom to account for footer
        if (listBottom - containerBottom < 150) {
          this.isLoadingMore = true;
          this.$emit('scrollEnd');
          this.timeoutId = setTimeout(() => {
            this.isLoadingMore = false;
          }, 1000);
        }
      }
    },
    handleSitterRecruited(sitterUuid: string | null) {
      this.$emit('sitterRecruited', sitterUuid);
    },
    showExtraContent(index: number): boolean {
      if (!this.showBHPartners) return false;
      return index === 4 || (this.searchResults.length < 5 && index === this.searchResults.length - 1);
    },
    // Both and childcare searches return babysitter and nanny profiles,
    // so we need to determine the correct caretype to display based on the sitter's profiles status
    caretypeForSitter(careType: string, sitter: any) {
      if (careType === 'nanny') {
        return sitter.nannyProfileVisible && sitter.nannyProfileCompleted ? 'nanny' : 'babysitter';
      }
      if (['babysitter', 'childcare', 'both'].includes(careType)) {
        return sitter.babysitterProfileVisible && sitter.babysitterProfileCompleted ? 'babysitter' : 'nanny';
      }
      return 'babysitter';
    },
  },
};
</script>
